/**
 * Cleanup duplicate transaction_types
 *
 * This utility identifies and removes duplicate transaction types
 * based on userId + name combination.
 *
 * Strategy: Keep the oldest document (earliest createdAt)
 */

import { collection, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';

export interface DuplicateGroup {
  key: string;
  userId: string;
  name: string;
  types: Array<{
    id: string;
    displayOrder: number;
    isActive: boolean;
    createdAt: Date;
  }>;
  toKeep: string; // Document ID to keep
  toDelete: string[]; // Document IDs to delete
}

/**
 * Get current logged-in user ID
 */
function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user logged in. Please login first.');
  }
  return user.uid;
}

/**
 * List all transaction_types for the current user (including inactive)
 * @param userId - Optional user ID. If not provided, uses currently logged-in user.
 */
export async function listAllTypes(userId?: string): Promise<Array<{
  id: string;
  name: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: Date;
}>> {
  const targetUserId = userId || getCurrentUserId();
  console.log('[listAllTypes] Fetching all types for userId:', targetUserId);

  const typesRef = collection(db, 'transaction_types');
  const q = query(typesRef, where('userId', '==', targetUserId));
  const snapshot = await getDocs(q);

  console.log('[listAllTypes] Total documents (including inactive):', snapshot.size);

  const types = snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      isActive: data.isActive,
      displayOrder: data.displayOrder,
      createdAt: data.createdAt?.toDate() || new Date(),
    };
  });

  // Sort by displayOrder
  types.sort((a, b) => a.displayOrder - b.displayOrder);

  console.table(types);

  return types;
}

/**
 * Check for duplicate transaction_types for the current user
 * @param userId - Optional user ID. If not provided, uses currently logged-in user.
 */
export async function checkDuplicateTypes(userId?: string): Promise<{
  duplicates: DuplicateGroup[];
  totalDuplicates: number;
}> {
  const targetUserId = userId || getCurrentUserId();
  console.log('[checkDuplicateTypes] Starting check for userId:', targetUserId);

  const typesRef = collection(db, 'transaction_types');
  // Check ALL types (not just active ones) for duplicates
  const q = query(typesRef, where('userId', '==', targetUserId));
  const snapshot = await getDocs(q);

  console.log('[checkDuplicateTypes] Total documents:', snapshot.size);

  // Group by name
  const groups = new Map<string, Array<{
    id: string;
    name: string;
    displayOrder: number;
    isActive: boolean;
    createdAt: Date;
  }>>();

  snapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    const type = {
      id: docSnapshot.id,
      name: data.name,
      displayOrder: data.displayOrder,
      isActive: data.isActive,
      createdAt: data.createdAt?.toDate() || new Date(),
    };

    if (!groups.has(type.name)) {
      groups.set(type.name, []);
    }
    groups.get(type.name)!.push(type);
  });

  // Find duplicates
  const duplicates: DuplicateGroup[] = [];
  let totalDuplicates = 0;

  groups.forEach((types, name) => {
    if (types.length > 1) {
      // Sort by createdAt (oldest first)
      const sorted = [...types].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

      const toKeep = sorted[0].id;
      const toDelete = sorted.slice(1).map((t) => t.id);

      duplicates.push({
        key: `${targetUserId}:${name}`,
        userId: targetUserId,
        name,
        types: sorted,
        toKeep,
        toDelete,
      });

      totalDuplicates += toDelete.length;
    }
  });

  console.log('[checkDuplicateTypes] Duplicate groups:', duplicates.length);
  console.log('[checkDuplicateTypes] Total extra documents:', totalDuplicates);

  return { duplicates, totalDuplicates };
}

/**
 * Remove duplicate transaction_types (DRY RUN)
 * @param userId - Optional user ID. If not provided, uses currently logged-in user.
 * @param dryRun - If true, only shows what would be deleted without actually deleting
 */
export async function removeDuplicateTypes(
  userId?: string,
  dryRun: boolean = true
): Promise<{
  success: boolean;
  deletedCount: number;
  errors: string[];
}> {
  const targetUserId = userId || getCurrentUserId();
  console.log('[removeDuplicateTypes] Starting cleanup (dryRun:', dryRun, ')');

  const { duplicates, totalDuplicates } = await checkDuplicateTypes(targetUserId);

  if (totalDuplicates === 0) {
    console.log('[removeDuplicateTypes] No duplicates found');
    return { success: true, deletedCount: 0, errors: [] };
  }

  console.log('[removeDuplicateTypes] Found', totalDuplicates, 'duplicates to remove');

  if (dryRun) {
    console.log('[removeDuplicateTypes] DRY RUN - No documents will be deleted');
    console.log('[removeDuplicateTypes] Would delete:');
    duplicates.forEach((group, index) => {
      console.log(`  ${index + 1}. ${group.name}:`);
      console.log(`     Keep: ${group.toKeep} (created: ${group.types[0].createdAt.toISOString()})`);
      group.toDelete.forEach((id, i) => {
        const type = group.types[i + 1];
        console.log(`     Delete: ${id} (created: ${type.createdAt.toISOString()})`);
      });
    });
    return { success: true, deletedCount: totalDuplicates, errors: [] };
  }

  // Execute deletion
  const errors: string[] = [];
  let deletedCount = 0;

  for (const group of duplicates) {
    for (const docId of group.toDelete) {
      try {
        await deleteDoc(doc(db, 'transaction_types', docId));
        deletedCount++;
        console.log('[removeDuplicateTypes] Deleted:', docId, `(${group.name})`);
      } catch (error) {
        const errorMsg = `Failed to delete ${docId}: ${error}`;
        console.error('[removeDuplicateTypes]', errorMsg);
        errors.push(errorMsg);
      }
    }
  }

  console.log('[removeDuplicateTypes] Cleanup complete');
  console.log('[removeDuplicateTypes] Deleted:', deletedCount);
  console.log('[removeDuplicateTypes] Errors:', errors.length);

  return {
    success: errors.length === 0,
    deletedCount,
    errors,
  };
}

// Note: Browser console helpers are exposed via cleanup-helper.ts
